import { useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { contentBriefPathes } from '../../../../../constants/queryPathes';
import { statusCodes } from '../../../../../constants/statusCodes';
import { QueueStatus } from '../../../../../constants/queueConsts';
import { defaultBriefOutlineContent, editorTypes } from '../../constants/tiptapEditorConstants';

import { axiosAPI } from '../../../../../utils/axiosAPI';
import { isCurrentOutlineExists } from '../../../../../utils/helpers/contentOptimizerHelpers';
import { getAxiosHeaders } from '../../../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../../../utils/helpers/openBasicErrorNotification';
import {
    getCurrentVersion,
    parseEditorContent,
    refactorEditorVersion,
    sortVersionsByDescendingDate,
} from '../../utils/versionHelpers';

import { setAIOutlineLoadingProgress } from '../../../../pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.actions';

import {
    addEditorVersion,
    deleteEditorVersion,
    setEditorPreviewVersion,
    updateCurrentEditorContent,
    updateCurrentEditorLoading,
    updateCurrentEditorVersion,
    updateEditorVersionName,
    updateEditorVersions,
} from '../../store/tiptapEditor.actions';
import {
    selectCurrentEditorContent,
    selectEditorVersions,
    selectEditorPreviewVersion,
} from '../../store/tiptapEditor.selectors';

const editorType = editorTypes.OUTLINE;

const useOutlineVersions = () => {
    const dispatch = useDispatch();

    const { contentOptimizerId } = useParams();

    const currentEditorContent = useSelector(selectCurrentEditorContent(editorType));
    const currentEditorVersions = useSelector(selectEditorVersions(editorType));
    const editorPreviewVersion = useSelector(selectEditorPreviewVersion(editorType));

    const [isVersionCreating, setIsVersionCreating] = useState(false);

    const createNewOutlineVersion = () => {
        if (contentOptimizerId) {
            setIsVersionCreating(true);

            return axiosAPI
                .post(
                    `${contentBriefPathes.getListOfContentBriefs}/${contentOptimizerId}/outlines`,
                    { outline: JSON.stringify(currentEditorContent || defaultBriefOutlineContent) },
                    { ...getAxiosHeaders() }
                )
                .then((res) => {
                    const resultData = res?.data;
                    const refactoredVersion = refactorEditorVersion(resultData, editorType);

                    dispatch(addEditorVersion({ editorType, version: refactoredVersion }));

                    return resultData;
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    setIsVersionCreating(false);
                });
        }
    };

    const fetchOutlineVersions = (updateEditorLoading = true) => {
        if (contentOptimizerId) {
            if (updateEditorLoading)
                dispatch(updateCurrentEditorLoading({ editorType: editorType, loading: true }));

            axiosAPI
                .get(
                    `${contentBriefPathes.getListOfContentBriefs}/${contentOptimizerId}/outlines`,
                    {
                        ...getAxiosHeaders(),
                    }
                )
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        if (result.data?.length > 0) {
                            const sortedVersions = sortVersionsByDescendingDate(result.data);
                            const refactoredVersions = sortedVersions.map((version) =>
                                refactorEditorVersion(version, editorType)
                            );
                            const currentVersion = getCurrentVersion(sortedVersions);
                            const currentContent = parseEditorContent(
                                currentVersion?.outline,
                                editorType
                            );

                            batch(() => {
                                dispatch(
                                    updateEditorVersions({
                                        editorType,
                                        versions: refactoredVersions,
                                    })
                                );
                                dispatch(
                                    updateCurrentEditorVersion({
                                        editorType,
                                        version: currentVersion,
                                    })
                                );
                                dispatch(
                                    updateCurrentEditorContent({
                                        editorType,
                                        content: isCurrentOutlineExists(currentContent, true)
                                            ? currentContent
                                            : defaultBriefOutlineContent,
                                    })
                                );
                                if (currentVersion.generation_status === QueueStatus.IN_PROGRESS) {
                                    dispatch(
                                        setAIOutlineLoadingProgress(
                                            currentVersion.generation_progress
                                        )
                                    );
                                }
                            });
                        } else {
                            createNewOutlineVersion();
                        }
                    }
                })
                .catch(({ message }) => {
                    openBasicErrorNotification(message);
                })
                .finally(() => {
                    dispatch(updateCurrentEditorLoading({ editorType, loading: false }));
                    setIsVersionCreating(false);
                });
        }
    };

    const handleActivateOutlineVersion = (outlineId) => {
        if (contentOptimizerId && outlineId) {
            dispatch(updateCurrentEditorLoading({ editorType, loading: true }));

            axiosAPI
                .patch(
                    `${contentBriefPathes.createContentBrief}/${contentOptimizerId}/outlines/${outlineId}`,
                    { active: true },
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    fetchOutlineVersions();
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    };

    const handlePreviewOutlineVersion = (versionId) => {
        const previewedVersionContent = currentEditorVersions.find(
            (version) => version.id === versionId
        ).content;

        dispatch(
            setEditorPreviewVersion({
                editorType,
                versionId: versionId && !editorPreviewVersion ? versionId : null,
                updateBasicData: false,
                versionContent: previewedVersionContent,
            })
        );
    };

    const handleUpdateVersionName = (versionId, versionName) => {
        if (contentOptimizerId && versionId) {
            axiosAPI
                .patch(
                    `${contentBriefPathes.createContentBrief}/${contentOptimizerId}/outlines/${versionId}`,
                    { name: versionName },
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    dispatch(
                        updateEditorVersionName({ editorType, id: versionId, name: versionName })
                    );
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    };

    const handleDeleteOutlineVersion = (versionId) => {
        if (contentOptimizerId && versionId) {
            axiosAPI
                .delete(
                    `${contentBriefPathes.deleteContentBrief}/${contentOptimizerId}/outlines/${versionId}`,
                    { ...getAxiosHeaders() }
                )
                .then(() => {
                    dispatch(deleteEditorVersion({ editorType, id: versionId }));
                })
                .catch(() => {
                    openBasicErrorNotification();
                });
        }
    };

    return {
        fetchOutlineVersions,
        createNewOutlineVersion,
        handleActivateOutlineVersion,
        handlePreviewOutlineVersion,
        handleUpdateVersionName,
        handleDeleteOutlineVersion,
        isVersionCreating,
        setIsVersionCreating,
    };
};

export default useOutlineVersions;
